// OperatorArchitectureWalkthroughPage.jsx
import React, { Fragment, useMemo } from "react";
import { motion } from "framer-motion";
import "./Styles.css";
import HeaderToMainPage from "../../Universal/Header/Index";
import Footer from "../../Universal/Footer/Index";
import JumbotronHero from "../../Universal/HeroJumbo/Index";
import { Link } from "react-router-dom";

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

const fade = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.5, ease: "easeOut" } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};

const PillTag = ({ children, tone = "cyan" }) => (
  <span className={`a-pill a-pill--${tone}`}>{children}</span>
);

const SectionHeader = ({ id, kicker, title, subtitle }) => (
  <div className="a-sectionHead" id={id}>
    {kicker ? <div className="a-kicker">{kicker}</div> : null}
    <h2 className="a-h2">{title}</h2>
    {subtitle ? <p className="a-lead">{subtitle}</p> : null}
  </div>
);

const Card = ({ children, className = "", as: As = "div", hover = true }) => {
  const Comp = hover ? motion[As] ?? motion.div : As;
  return (
    <Comp
      className={`a-card ${className}`}
      {...(hover
        ? {
            whileHover: { y: -3, transition: { duration: 0.18 } },
            whileTap: { y: -1, transition: { duration: 0.12 } },
          }
        : {})}
    >
      {children}
    </Comp>
  );
};

const Checklist = ({ title, items, icon = "check" }) => (
  <div className="a-checklist">
    {title ? <div className="a-checklistTitle">{title}</div> : null}
    <ul className="a-checklistList">
      {items.map((t, i) => (
        <li key={`${t}-${i}`} className="a-checkItem">
          <span className={`a-bulletIcon a-bulletIcon--${icon}`} aria-hidden="true">
            {icon === "dot" ? "" : "✓"}
          </span>
          <span className="a-checkText">{t}</span>
        </li>
      ))}
    </ul>
  </div>
);

const DiagramCard = ({ title, lines, note }) => (
  <Card className="a-card--diagram" hover={false}>
    <div className="a-cardTop">
      <div className="a-cardTitle">{title}</div>
      <span className="a-chip">Text Diagram</span>
    </div>
    <div className="a-diagram">
      {lines.map((l, i) => (
        <div key={`${l}-${i}`} className="a-diagramLine">
          {l}
        </div>
      ))}
    </div>
    {note ? <div className="a-diagramNote">{note}</div> : null}
  </Card>
);

const Stepper = ({ title, steps }) => (
  <Card className="a-card--stepper" hover={false}>
    <div className="a-cardTop">
      <div className="a-cardTitle">{title}</div>
      <span className="a-chip a-chip--violet">Lifecycle</span>
    </div>
    <ol className="a-stepper">
      {steps.map((s, i) => (
        <li key={`${s}-${i}`} className="a-step">
          <div className="a-stepIndex" aria-hidden="true">
            {i + 1}
          </div>
          <div className="a-stepBody">
            <div className="a-stepTitle">{s.title}</div>
            <div className="a-stepDesc">{s.desc}</div>
          </div>
        </li>
      ))}
    </ol>
  </Card>
);

const Timeline = ({ title, items }) => (
  <Card className="a-card--timeline" hover={false}>
    <div className="a-cardTop">
      <div className="a-cardTitle">{title}</div>
      <span className="a-chip a-chip--green">Artifacts</span>
    </div>
    <div className="a-timeline">
      {items.map((it, i) => (
        <div key={`${it}-${i}`} className="a-tlRow">
          <div className="a-tlDot" aria-hidden="true" />
          <div className="a-tlLine" aria-hidden="true" />
          <div className="a-tlContent">
            <div className="a-tlTitle">{it.title}</div>
            <div className="a-tlDesc">{it.desc}</div>
          </div>
        </div>
      ))}
    </div>
  </Card>
);

const StickyMiniNav = ({ items }) => (
  <nav className="a-miniNav" aria-label="Architecture walkthrough navigation">
    <div className="a-miniNavInner">
      <div className="a-miniNavLabel">On this page</div>
      <div className="a-miniNavLinks">
        {items.map((it) => (
          <a key={it.href} className="a-miniNavLink" href={it.href}>
            <span className="a-miniNavDot" aria-hidden="true" />
            {it.label}
          </a>
        ))}
      </div>
    </div>
  </nav>
);

const OperatorArchitectureWalkthroughHelper = () => {
  const heroTags = useMemo( 
    () => [
      { t: "Multi-Tenant Control Plane", tone: "cyan" },
      { t: "Wallet & Ledger Integrity", tone: "green" },
      { t: "Provably Fair Verification", tone: "violet" },
      { t: "Audit-Grade Traceability", tone: "red" },
    ],
    []
  );

  const miniNav = useMemo(
    () => [
      { label: "System Map", href: "#system-map" },
      { label: "Tenant Boundaries", href: "#tenant-boundaries" },
      { label: "Wallet Boundaries", href: "#wallets" },
      { label: "Fairness Artifacts", href: "#round-fairness" },
      { label: "Auditability", href: "#auditability" },
    ],
    []
  );

  const systemModules = useMemo(
    () => [
      { title: "Edge + Tenant Routing", desc: "Tenant-aware request context, policy gates, and operator boundary propagation." },
      { title: "Identity + Roles", desc: "Operator staff roles, scoped permissions, and governed administrative actions." },
      { title: "Game Catalog + Entitlements", desc: "Per-tenant enablement, tier/subscription gating, and server-side access enforcement." },
      { title: "Wallets + Ledger Layer", desc: "Balance boundaries, ledger-backed mutations, holds/locks, and settlement traceability." },
      { title: "Round Engine + Settlement", desc: "Server-authoritative round lifecycle and economically consistent state transitions." },
      { title: "Fairness Artifacts + Verifier UI", desc: "Commit–reveal posture, replay artifacts, two-click verification, and sandbox review surfaces." },
      { title: "Logging + Exports", desc: "Audit-grade record posture, export bundles, and operator-ready reconciliation artifacts." },
      { title: "Operator Control Plane", desc: "Dashboards, governance controls, risk posture tools, and tenant-scoped operational visibility." },
    ],
    []
  );

  const lifecycleStrip = useMemo(
    () => [
      "Tenant context",
      "Authentication",
      "Entitlement check",
      "Bet lock",
      "Round execution",
      "Settlement",
      "Artifact logging",
      "Optional replay verification",
    ],
    []
  );

  const tenantExplainers = useMemo(
    () => [
      {
        title: "Operator boundary by design",
        desc:
          "A tenant represents an operator boundary. Configuration, policies, entitlements, and audit artifacts are scoped to that boundary so operators can share infrastructure without sharing authority.",
      },
      {
        title: "Tenant context is non-optional",
        desc:
          "Requests are evaluated within an explicit tenant context. The platform treats tenant identity as a first-class input to authorization, economic enforcement, and artifact generation.",
      },
      {
        title: "Policies and catalogs are scoped",
        desc:
          "Game catalogs, limits, governance controls, and retention rules are applied per tenant. This enables operators to run distinct business models without forking the platform.",
      },
      {
        title: "Independent auditability",
        desc:
          "Fairness artifacts, settlement references, and operational logs are scoped to the tenant boundary—supporting diligence, dispute resolution, and review without cross-operator coupling.",
      },
    ],
    []
  );

  const tenantPseudo = useMemo(
    () => [
      "Tenant",
      " ├─ Identity (operator boundary)",
      " ├─ Domain / branding profile",
      " ├─ Enabled games + entitlement gates",
      " ├─ Limits + policy templates",
      " ├─ Roles + permission scopes",
      " ├─ Retention + verifier availability policies",
      " └─ Configuration snapshots (audit posture)",
    ],
    []
  );

  const roles = useMemo(
    () => [
      { role: "Operator Admin", desc: "Configures catalog, limits, governance controls, and operational tooling within the tenant boundary." },
      { role: "Compliance / Auditor", desc: "Reviews verification artifacts, export bundles, and administrative traceability with scoped access." },
      { role: "Risk / Operations", desc: "Monitors exposure posture, policy adherence, and operational signals with role-bound permissions." },
      { role: "Support", desc: "Uses sandbox review and log-linked records for dispute workflows without privileged access to sensitive materials." },
    ],
    []
  );

  const governanceNotes = useMemo(
    () => [
      "Permissions are scoped by role and tenant context.",
      "Administrative actions produce traceable records (who / what / when).",
      "Review workflows support separation between operations and audit roles.",
      "Configuration changes are treated as governed events, not silent edits.",
    ],
    []
  );

  const entitlementControls = useMemo(
    () => [
      "Per-tenant per-game enable/disable",
      "Tier/subscription gating (server-side)",
      "Policy-driven limits by game category",
      "Verifier surface availability controls",
      "Operational override workflows with traceability",
    ],
    []
  );

  const entitlementOutcomes = useMemo(
    () => [
      "Clear governance for multi-team operators",
      "Predictable scaling without platform forks",
      "Reduced vendor coupling for core logic",
      "Cleaner diligence story for partners",
      "Operational consistency across brands",
    ],
    []
  );

  const walletPrinciples = useMemo(
    () => [
      "Balance types represent separate accounting boundaries.",
      "Mutations are ledger-backed and traceable.",
      "Holds/locks are used during round execution to protect settlement integrity.",
      "No commingling across balance boundaries by design posture.",
      "Settlement links economic events to verifiable round artifacts.",
    ],
    []
  );

  const walletDiagram = useMemo(
    () => [
      "Real Balance Wallet  ↔  Ledger Entries  ↔  Settlement",
      "Sweepstakes/Promo Wallet  ↔  Separate Ledger  ↔  Settlement",
    ],
    []
  );

  const settlementExplainers = useMemo(
    () => [
      { title: "Pre-execution bet lock", desc: "Economic intent is captured before round execution so settlement remains consistent and reviewable." },
      { title: "Atomic settlement posture", desc: "Outcome finalization and economic mutation are treated as a single consistent state transition at a system level." },
      { title: "Idempotent handling posture", desc: "Duplicate submissions and retries are handled as safe repeats of the same intent, not as new economic events." },
      { title: "Reconciliation readiness", desc: "Ledger references and audit artifacts support review workflows without relying on undocumented assumptions." },
    ],
    []
  );

  const settlementDiagram = useMemo(
    () => ["Wallet → Bet Lock → Round → Settlement → Ledger Commit → Audit Log"],
    []
  );

  const depositFlows = useMemo(
    () => [
      {
        title: "Crypto Funding Path",
        items: [
          "Tenant-scoped funding context and policy gates",
          "Address/intent assignment (conceptual) with confirmation posture",
          "Credit rules applied with traceable references",
          "Operational visibility for review and reconciliation",
          "Audit records linking funding events to ledger entries",
        ],
        note:
          "Provider-specific mechanics are intentionally abstracted here. This walkthrough focuses on observable system behavior and auditability.",
      },
      {
        title: "Fiat / Sweepstakes Funding Path",
        items: [
          "Provider-abstracted purchase flow and eligibility posture",
          "Separate balance boundary for promotional/sweepstakes accounting",
          "Entitlement-aware gating and tenant policy application",
          "Clear linking between purchase intent and ledger-backed credit",
          "Export-ready records for review workflows",
        ],
        note:
          "This section describes platform boundaries, not payment-provider internals.",
      },
    ],
    []
  );

  const withdrawalChecklist = useMemo(
    () => [
      "Request captured within tenant context",
      "Validation against policy gates and role permissions",
      "Review posture where required (high-level)",
      "Execution via approved payout method",
      "Confirmation linking to ledger references",
      "Audit record suitable for reconciliation and dispute workflows",
    ],
    []
  );

  const redemptionNotes = useMemo(
    () => [
      "Crypto withdrawals are policy-gated and logged with traceable references.",
      "Gift card redemptions are modeled as controlled payout methods with provider abstraction.",
      "Workflows are designed to support operational review without exposing sensitive internals.",
    ],
    []
  );

  const fairnessStepper = useMemo(
    () => [
      { title: "Commitment recorded", desc: "A commitment posture is established prior to outcome resolution to support independent verification." },
      { title: "Inputs captured", desc: "Round inputs are captured and scoped to tenant and round context for reproducibility." },
      { title: "Outcome resolved server-side", desc: "Authoritative outcome resolution occurs in the engine layer, not in the client." },
      { title: "Settlement committed", desc: "Economic mutation is linked to the resolved round and recorded with audit posture." },
      { title: "Verification available", desc: "Replay surfaces can reproduce the round outcome from committed artifacts without exposing proprietary logic." },
    ],
    []
  );

  const verificationTimeline = useMemo(
    () => [
      { title: "Round created", desc: "A round is created with tenant and session context." },
      { title: "Commitment recorded", desc: "Commitment posture is established before outcome resolution." },
      { title: "Bet accepted", desc: "Economic intent is validated and locked at a system level." },
      { title: "Outcome settled", desc: "The engine resolves outcome and commits settlement traceably." },
      { title: "Verifier available", desc: "Sandbox replay and verification artifacts are available for review." },
      { title: "Export possible", desc: "Operator-ready artifacts can be bundled for diligence or dispute workflows." },
    ],
    []
  );

  const auditLogged = useMemo(
    () => [
      "Round lifecycle state transitions (high-level)",
      "Settlement references linking outcome to economic mutation",
      "Verifier availability and replay metadata (conceptual)",
      "Administrative actions (who/what/when) within role scopes",
      "Configuration snapshots and governed change records",
      "Policy gate evaluations at a system level (conceptual)",
    ],
    []
  );

  const auditExported = useMemo(
    () => [
      "Fairness verification bundles (artifact references, not secrets)",
      "Round history views with traceable identifiers",
      "Ledger-linked reconciliation exports",
      "Administrative action histories for diligence review",
      "Configuration history summaries (high-level)",
      "Tenant-scoped operational reporting extracts",
    ],
    []
  );

  const securityPosture = useMemo(
    () => [
      { title: "Server-authoritative control", desc: "Clients render results; they do not produce authoritative outcomes or settlement mutations." },
      { title: "Tenant isolation", desc: "Tenant context is treated as a boundary input to authorization, policy gates, and artifact scoping." },
      { title: "Rate limiting posture", desc: "Traffic controls help protect the system from abusive patterns and operational instability." },
      { title: "Duplicate/replay mitigation", desc: "Round identifiers, sequencing posture, and validation gates reduce the risk of duplicate submissions." },
      { title: "Secrets not exposed to clients", desc: "Sensitive materials remain server-side; client surfaces receive only what is required to render and verify." },
    ],
    []
  );

  const deployment = useMemo(
    () => [
      { title: "Environment separation", desc: "Operational environments are separated conceptually to support controlled changes, review, and safe iteration." },
      { title: "Tenant-aware scaling", desc: "Scaling posture accounts for tenant boundaries and noisy-neighbor controls to preserve operator stability." },
      { title: "Observability pipelines", desc: "Telemetry and reporting are structured to support tenant-scoped visibility and operational review." },
      { title: "Upgrade posture", desc: "Versioned behavior and governed changes support predictability across operators without ad-hoc forks." },
    ],
    []
  );

  return (
    <Fragment>
      <HeaderToMainPage />
      <JumbotronHero
        kicker="PLATFORM DOCUMENTATION • MULTI-TENANT INFRASTRUCTURE"
        title={
          <>
            Architecture <span className="jh-accent">Walkthrough</span> — Built for{" "}
            <span className="jh-accent2">Verification and Control</span>.
          </>
        }
        subtitle="This walkthrough outlines Ethosino’s system architecture across tenant provisioning, entitlement enforcement, wallet and ledger boundaries, provably-fair seed lifecycle, two-click verification tooling, and audit-grade logging. It is a system-level overview designed for operator diligence and technical review—focused on how the platform enforces determinism, isolation, and reproducibility across every round and tenant."
        primaryCta={{ label: "Request Architecture Brief", href: "/architecture-brief" }}
        secondaryCta={{ label: "View Fairness Module", href: "/provably-fair-engine" }}
        checklistTitle="Architecture Layers Covered"
        checklist={[
          "Multi-tenant provisioning and isolated operator boundaries",
          "Server-authoritative round engine and deterministic settlement lifecycle",
          "Wallet separation: real balance vs sweepstakes/promo accounting",
          "Commit–reveal fairness model with serverSeedHash commitments",
          "ClientSeed + nonce sequencing for reproducible outcomes",
          "Two-click verification and sandboxed replay reconstruction",
          "Tenant-scoped audit artifacts and exportable verification bundles",
          "Role-based governance and administrative traceability"
        ]}
        bgImage="/assets/images/hero/diamonds.png"
      />
        <div className="a-page">
          {/* Ambient overlays */}
          <div className="a-bg" aria-hidden="true">
            <div className="a-bgGlow a-bgGlow--cyan" />
            <div className="a-bgGlow a-bgGlow--violet" />
            <div className="a-bgGlow a-bgGlow--green" />
            <div className="a-bgGrid" />
            <div className="a-bgNoise" />
            <div className="a-scanlines" />
            <div className="a-vignette" />
          </div>

          <StickyMiniNav items={miniNav} />

          {/* HERO */}
          <section className="a-hero">
            <div className="a-container">
              <motion.div className="a-heroInner" initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }} variants={stagger}>
                <motion.div className="a-heroLeft" variants={fadeUp}>
                  <div className="a-heroKicker">
                    <span className="a-kDot" aria-hidden="true" />
                    <span>Platform</span>
                    <span className="a-kSep">/</span>
                    <span>Architecture Walkthrough</span>
                  </div>

                  <h1 className="a-h1">
                    Architecture <span className="a-accent">Walkthrough</span>.
                  </h1>

                  <p className="a-subhead">
                    This page is a system-level walkthrough of Ethosino’s multi-tenant platform: identities, wallet boundaries, game access control, fairness artifacts, and audit readiness—written for operator diligence.
                  </p>

                  <div className="a-pillRow">
                    {heroTags.map((x) => (
                      <PillTag key={x.t} tone={x.tone}>
                        {x.t}
                      </PillTag>
                    ))}
                  </div>

                  <div className="a-heroActions">
                    <a className="a-btn a-btn--primary" href="/request-architecture-brief">
                      Request Architecture Brief <span className="a-arr" aria-hidden="true">→</span>
                    </a>
                    <a className="a-btn a-btn--ghost" href="/platform/fairness">
                      View Fairness Module <span className="a-arr" aria-hidden="true">→</span>
                    </a>
                  </div>

                  <div className="a-heroNote">
                    <div className="a-heroNoteTitle">Scope note</div>
                    <div className="a-heroNoteBody">
                      This walkthrough focuses on boundaries, flows, and auditability. It intentionally avoids implementation-sensitive details such as schemas, routes, secret handling mechanics, and other attack-surface mappings.
                    </div>
                  </div>
                </motion.div>

                <motion.div className="a-heroRight" variants={fadeUp}>
                  <Card className="a-card--heroPanel" hover={false}>
                    <div className="a-heroPanelTop">
                      <div className="a-panelTitle">Walkthrough boundaries</div>
                      <div className="a-panelChips">
                        <span className="a-chip a-chip--cyan">Operator diligence</span>
                        <span className="a-chip a-chip--violet">Security-safe</span>
                      </div>
                    </div>

                    <div className="a-heroSplit">
                      <div className="a-heroCol">
                        <div className="a-heroColTitle">Includes</div>
                        <ul className="a-heroList">
                          <li><span className="a-liMark">✓</span> Tenant scoping and control-plane model</li>
                          <li><span className="a-liMark">✓</span> Wallet boundary and settlement posture</li>
                          <li><span className="a-liMark">✓</span> Funding and withdrawal pathways (conceptual)</li>
                          <li><span className="a-liMark">✓</span> Provably fair artifacts and verifier UX</li>
                          <li><span className="a-liMark">✓</span> Audit exports and operational traceability</li>
                        </ul>
                      </div>

                      <div className="a-heroCol a-heroCol--avoid">
                        <div className="a-heroColTitle">Intentionally avoids</div>
                        <ul className="a-heroList">
                          <li><span className="a-liMark a-liMark--x">×</span> Exact schemas and complete field listings</li>
                          <li><span className="a-liMark a-liMark--x">×</span> Endpoint paths, routes, internal file structure</li>
                          <li><span className="a-liMark a-liMark--x">×</span> Secret storage and key rotation mechanics</li>
                          <li><span className="a-liMark a-liMark--x">×</span> Formulas, exploit routes, bypass instructions</li>
                          <li><span className="a-liMark a-liMark--x">×</span> Provider-specific internals for payments/payouts</li>
                        </ul>
                      </div>
                    </div>

                    <div className="a-heroPanelFooter">
                      <div className="a-footLabel">Conceptual data model</div>
                      <div className="a-footModel">
                        Tenant → User → Wallet → Ledger Entry → Round Artifact
                      </div>
                    </div>
                  </Card>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* SYSTEM MAP */}
          <section className="a-section" id="system-map">
            <div className="a-container">
              <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.18 }} variants={stagger}>
                <motion.div variants={fadeUp}>
                  <SectionHeader
                    kicker="Architecture overview"
                    title="System Map."
                    subtitle="Ethosino is structured as a tenant-aware platform where policy gates, economic enforcement, and verification artifacts are first-class behaviors—not afterthoughts."
                  />
                </motion.div>

                <motion.div className="a-grid a-grid--modules" variants={stagger}>
                  {systemModules.map((m) => (
                    <motion.div key={m.title} variants={fadeUp}>
                      <Card className="a-card--module">
                        <div className="a-cardTitleRow">
                          <div className="a-cardTitle">{m.title}</div>
                          <span className="a-miniGlow" aria-hidden="true" />
                        </div>
                        <div className="a-body">{m.desc}</div>
                      </Card>
                    </motion.div>
                  ))}
                </motion.div>

                <motion.div className="a-lifecycle" variants={fadeUp}>
                  <div className="a-lifecycleHead">
                    <div className="a-lifecycleTitle">Request lifecycle (conceptual)</div>
                    <div className="a-lifecycleHint">Boundaries shown at a system level; no routes or internal names.</div>
                  </div>
                  <div className="a-lifecycleStrip" role="list">
                    {lifecycleStrip.map((s, i) => (
                      <div className="a-lifeNode" role="listitem" key={`${s}-${i}`}>
                        <span className="a-lifeIndex" aria-hidden="true">{i + 1}</span>
                        <span className="a-lifeText">{s}</span>
                        {i < lifecycleStrip.length - 1 ? <span className="a-lifeArrow" aria-hidden="true">→</span> : null}
                      </div>
                    ))}
                  </div>
                </motion.div>

                <motion.div className="a-grid a-grid--diagram" variants={fadeUp}>
                  <DiagramCard
                    title="Module relationship diagram"
                    lines={[
                      "Edge/Tenant Context → Identity & Roles → Entitlement Gates",
                      "Entitlement Gates → Wallet & Ledger → Round Engine",
                      "Round Engine → Settlement → Fairness Artifacts",
                      "Fairness Artifacts → Verifier UI / Sandbox Review",
                      "All Flows → Logging & Exports → Operator Control Plane",
                    ]}
                    note="This diagram describes responsibility boundaries and artifact flow. It does not map internal routes, schemas, or secret handling."
                  />
                  <Card className="a-card--callout" hover={false}>
                    <div className="a-cardTop">
                      <div className="a-cardTitle">Design intent</div>
                      <span className="a-chip a-chip--red">Trust posture</span>
                    </div>
                    <div className="a-body">
                      The platform is designed so that outcomes and economic mutations produce reproducible artifacts suitable for review. Operators shouldn’t depend on external black boxes to explain what happened in a round.
                    </div>
                    <div className="a-softDivider" />
                    <div className="a-miniRow">
                      <div className="a-miniLabel">Operator benefit</div>
                      <div className="a-miniValue">Clean diligence story + consistent audit narrative</div>
                    </div>
                  </Card>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* TENANT BOUNDARIES */}
          <section className="a-section" id="tenant-boundaries">
            <div className="a-container">
              <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.18 }} variants={stagger}>
                <motion.div variants={fadeUp}>
                  <SectionHeader
                    kicker="Multi-tenant model"
                    title="Tenant Boundaries."
                    subtitle="A tenant is an operator boundary. The platform treats tenant context as a first-class input to authorization, policy evaluation, accounting boundaries, and verification artifact scoping."
                  />
                </motion.div>

                <motion.div className="a-grid a-grid--two" variants={stagger}>
                  <motion.div variants={fadeUp}>
                    <Card className="a-card--article" hover={false}>
                      <div className="a-articleTitle">Isolation as an operational requirement</div>
                      <p className="a-body">
                        Multi-tenancy is only useful if it preserves clean boundaries under real operator pressure: multiple brands, multiple teams, different policies, and different risk postures.
                        Ethosino models the tenant boundary as a perimeter for governance controls, economic enforcement, and verification artifacts—so the operator experience remains auditable and consistent as volume grows.
                      </p>
                      <p className="a-body">
                        This approach supports scaling without forking: operators adjust catalogs, limits, entitlements, and retention policies through governed controls rather than code-level divergence.
                        The goal is predictable behavior and reviewability across tenants, rounds, and user sessions.
                      </p>
                      <div className="a-softDivider" />
                      <div className="a-miniRow">
                        <div className="a-miniLabel">Boundary rule</div>
                        <div className="a-miniValue">Tenant identity is never optional for authoritative actions.</div>
                      </div>
                    </Card>
                  </motion.div>

                  <motion.div variants={fadeUp}>
                    <Card className="a-card--stack" hover={false}>
                      <div className="a-cardTop">
                        <div className="a-cardTitle">Tenant boundary properties</div>
                        <span className="a-chip a-chip--cyan">Operator perimeter</span>
                      </div>
                      <div className="a-stack">
                        {tenantExplainers.map((x) => (
                          <div className="a-stackItem" key={x.title}>
                            <div className="a-stackHead">
                              <span className="a-stackMark" aria-hidden="true" />
                              <div className="a-stackTitle">{x.title}</div>
                            </div>
                            <div className="a-stackDesc">{x.desc}</div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </motion.div>
                </motion.div>

                <motion.div className="a-grid a-grid--diagram" variants={fadeUp}>
                  <DiagramCard
                    title="Tenant object (redacted pseudo-structure)"
                    lines={tenantPseudo}
                    note="Labels only. This is not a schema and does not list complete fields or internal representations."
                  />
                  <Card className="a-card--note" hover={false}>
                    <div className="a-cardTop">
                      <div className="a-cardTitle">Why this matters</div>
                      <span className="a-chip a-chip--violet">Due diligence</span>
                    </div>
                    <div className="a-body">
                      Operators and partners evaluate whether a platform can prove boundaries: who can change policies, how economic events are recorded, and whether verification artifacts remain scoped and reproducible.
                      Tenant-first design reduces cross-operator coupling and supports cleaner audit narratives.
                    </div>
                  </Card>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* IDENTITY & GOVERNANCE */}
          <section className="a-section" id="governance">
            <div className="a-container">
              <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.18 }} variants={stagger}>
                <motion.div variants={fadeUp}>
                  <SectionHeader
                    kicker="Identity and control"
                    title="Identity & Governance."
                    subtitle="Ethosino is built for operational teams. Governance is expressed through role-scoped permissions, tenant context propagation, and traceable administrative actions."
                  />
                </motion.div>

                <motion.div className="a-grid a-grid--roles" variants={stagger}>
                  {roles.map((r) => (
                    <motion.div key={r.role} variants={fadeUp}>
                      <Card className="a-card--role">
                        <div className="a-roleTop">
                          <div className="a-roleTitle">{r.role}</div>
                          <span className="a-rolePill">Tenant-scoped</span>
                        </div>
                        <div className="a-body">{r.desc}</div>
                      </Card>
                    </motion.div>
                  ))}
                </motion.div>

                <motion.div className="a-grid a-grid--diagram" variants={fadeUp}>
                  <Card className="a-card--govern" hover={false}>
                    <div className="a-cardTop">
                      <div className="a-cardTitle">Governance posture (high-level)</div>
                      <span className="a-chip a-chip--green">Traceability</span>
                    </div>
                    <Checklist title={null} items={governanceNotes} />
                    <div className="a-softDivider" />
                    <div className="a-miniRow">
                      <div className="a-miniLabel">Audit outcome</div>
                      <div className="a-miniValue">Operator changes are attributable and reviewable without exposing sensitive internals.</div>
                    </div>
                  </Card>

                  <Card className="a-card--callout" hover={false}>
                    <div className="a-cardTop">
                      <div className="a-cardTitle">Operational separation</div>
                      <span className="a-chip a-chip--red">Governed</span>
                    </div>
                    <div className="a-body">
                      Real operators require separation between duties: operations, compliance, risk review, and support workflows. The system is designed to make this separation natural—so reviewable actions remain scoped and intentional.
                    </div>
                    <div className="a-softDivider" />
                    <div className="a-miniRow">
                      <div className="a-miniLabel">Principle</div>
                      <div className="a-miniValue">Least privilege by role + tenant context.</div>
                    </div>
                  </Card>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ENTITLEMENT CONTROL */}
          <section className="a-section" id="entitlements">
            <div className="a-container">
              <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.18 }} variants={stagger}>
                <motion.div variants={fadeUp}>
                  <SectionHeader
                    kicker="Catalog and gating"
                    title="Game Access Enforcement."
                    subtitle="Games are modular. Access is enforced server-side through tenant-scoped entitlements and tier/subscription validation. The UI can reflect access—but it never authorizes it."
                  />
                </motion.div>

                <motion.div className="a-grid a-grid--two" variants={stagger}>
                  <motion.div variants={fadeUp}>
                    <Card className="a-card--split" hover={false}>
                      <div className="a-splitHead">
                        <div className="a-cardTitle">Controls</div>
                        <span className="a-chip a-chip--cyan">Governance</span>
                      </div>
                      <Checklist title={null} items={entitlementControls} />
                    </Card>
                  </motion.div>

                  <motion.div variants={fadeUp}>
                    <Card className="a-card--split" hover={false}>
                      <div className="a-splitHead">
                        <div className="a-cardTitle">Operational outcomes</div>
                        <span className="a-chip a-chip--violet">Stability</span>
                      </div>
                      <Checklist title={null} items={entitlementOutcomes} />
                    </Card>
                  </motion.div>
                </motion.div>

                <motion.div className="a-callStrip" variants={fadeUp}>
                  <div className="a-callStripInner">
                    <div className="a-callStripTitle">Key point</div>
                    <div className="a-callStripBody">
                      Entitlement enforcement is performed where it matters: at authoritative boundaries. This reduces reliance on client-side assumptions and supports clean operator control.
                    </div>
                    <a className="a-inlineLink" href="/pricing">
                      See Pricing & Licensing <span aria-hidden="true">→</span>
                    </a>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* WALLET BOUNDARIES */}
          <section className="a-section" id="wallets">
            <div className="a-container">
              <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.18 }} variants={stagger}>
                <motion.div variants={fadeUp}>
                  <SectionHeader
                    kicker="Economic model"
                    title="Wallets: Real vs Sweepstakes."
                    subtitle="Ethosino supports separate balance boundaries for real value (cash/crypto) and sweepstakes/promotional balances. Separation supports clear accounting posture, review workflows, and audit narratives."
                  />
                </motion.div>

                <motion.div className="a-grid a-grid--two" variants={stagger}>
                  <motion.div variants={fadeUp}>
                    <Card className="a-card--article" hover={false}>
                      <div className="a-articleTitle">Balance boundaries are not cosmetic</div>
                      <p className="a-body">
                        Different balance types represent different accounting boundaries and operational rules. Ethosino treats these boundaries as first-class so that economic events remain traceable, reviewable, and consistent across funding, gameplay, settlement, and payout workflows.
                      </p>
                      <p className="a-body">
                        The platform emphasizes ledger-backed mutations, holds/locks during execution, and traceable settlement references. This posture supports operator confidence and reduces ambiguity during diligence or dispute review.
                      </p>
                      <div className="a-softDivider" />
                      <Checklist title="Wallet boundary principles" items={walletPrinciples} />
                    </Card>
                  </motion.div>

                  <motion.div variants={fadeUp}>
                    <DiagramCard
                      title="Wallet boundary diagram (conceptual)"
                      lines={walletDiagram}
                      note="This diagram shows boundaries and references. It does not disclose ledger schemas, provider internals, or enforcement mechanics."
                    />
                    <Card className="a-card--note a-card--tight" hover={false}>
                      <div className="a-cardTop">
                        <div className="a-cardTitle">Why separation matters</div>
                        <span className="a-chip a-chip--green">Audit-ready</span>
                      </div>
                      <div className="a-body">
                        Separation improves clarity: operators can reason about funding sources, settlement, and withdrawal posture without ambiguity. It also supports cleaner export bundles for reconciliation and review.
                      </div>
                    </Card>
                  </motion.div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* LEDGER & SETTLEMENT */}
          <section className="a-section" id="ledger-settlement">
            <div className="a-container">
              <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.18 }} variants={stagger}>
                <motion.div variants={fadeUp}>
                  <SectionHeader
                    kicker="Settlement integrity"
                    title="Ledger & Settlement Layer."
                    subtitle="The settlement layer is designed to produce economically consistent state transitions. Bets lock funds before execution, settlement is committed with traceable references, and reconciliation posture remains reviewable."
                  />
                </motion.div>

                <motion.div className="a-grid a-grid--modules" variants={stagger}>
                  {settlementExplainers.map((x) => (
                    <motion.div key={x.title} variants={fadeUp}>
                      <Card className="a-card--module">
                        <div className="a-cardTitleRow">
                          <div className="a-cardTitle">{x.title}</div>
                          <span className="a-miniGlow a-miniGlow--green" aria-hidden="true" />
                        </div>
                        <div className="a-body">{x.desc}</div>
                      </Card>
                    </motion.div>
                  ))}
                </motion.div>

                <motion.div className="a-grid a-grid--diagram" variants={fadeUp}>
                  <DiagramCard
                    title="Settlement path (pseudo-diagram)"
                    lines={settlementDiagram}
                    note="No locking strategy, no transaction internals. This describes the conceptual economic flow at a system boundary level."
                  />
                  <Card className="a-card--callout" hover={false}>
                    <div className="a-cardTop">
                      <div className="a-cardTitle">Consistency goal</div>
                      <span className="a-chip a-chip--violet">Correctness</span>
                    </div>
                    <div className="a-body">
                      The aim is not simply to “pay out.” The aim is to ensure each round results in a consistent, reviewable economic record that can be referenced during reconciliation, support, and diligence workflows.
                    </div>
                    <div className="a-softDivider" />
                    <div className="a-miniRow">
                      <div className="a-miniLabel">Traceability</div>
                      <div className="a-miniValue">Outcome ↔ settlement ↔ ledger reference ↔ audit posture.</div>
                    </div>
                  </Card>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* DEPOSITS */}
          <section className="a-section" id="deposits">
            <div className="a-container">
              <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.18 }} variants={stagger}>
                <motion.div variants={fadeUp}>
                  <SectionHeader
                    kicker="Funding paths"
                    title="Deposits & Funding Paths."
                    subtitle="Ethosino supports multiple funding paths. This section explains the platform posture for observability, policy gating, and auditability—without provider internals."
                  />
                </motion.div>

                <motion.div className="a-grid a-grid--two" variants={stagger}>
                  {depositFlows.map((f) => (
                    <motion.div key={f.title} variants={fadeUp}>
                      <Card className="a-card--flow" hover={false}>
                        <div className="a-cardTop">
                          <div className="a-cardTitle">{f.title}</div>
                          <span className="a-chip a-chip--cyan">Conceptual</span>
                        </div>
                        <Checklist title={null} items={f.items} />
                        <div className="a-softDivider" />
                        <div className="a-flowNote">{f.note}</div>
                      </Card>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* WITHDRAWALS */}
          <section className="a-section" id="withdrawals">
            <div className="a-container">
              <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.18 }} variants={stagger}>
                <motion.div variants={fadeUp}>
                  <SectionHeader
                    kicker="Payout posture"
                    title="Withdrawals & Redemptions."
                    subtitle="Withdrawals and redemptions are policy-gated and logged. The platform is designed to provide traceable workflows suitable for operational review and reconciliation."
                  />
                </motion.div>

                <motion.div className="a-grid a-grid--diagram" variants={fadeUp}>
                  <Card className="a-card--article" hover={false}>
                    <div className="a-articleTitle">Controlled payout methods</div>
                    <p className="a-body">
                      Ethosino supports crypto withdrawals and controlled redemption pathways (such as gift card redemption marketplaces). The platform emphasizes policy gates, role-scoped review posture, and ledger-linked traceability.
                    </p>
                    <p className="a-body">
                      This walkthrough stays at a system boundary level. It describes observable behavior and audit posture without provider-specific implementation details or operational mechanics.
                    </p>
                    <div className="a-softDivider" />
                    <Checklist title="Withdrawal posture checklist" items={withdrawalChecklist} />
                  </Card>

                  <Card className="a-card--note" hover={false}>
                    <div className="a-cardTop">
                      <div className="a-cardTitle">Redemption notes (high-level)</div>
                      <span className="a-chip a-chip--green">Traceable</span>
                    </div>
                    <ul className="a-noteList">
                      {redemptionNotes.map((n, i) => (
                        <li key={`${n}-${i}`} className="a-noteItem">
                          <span className="a-noteDot" aria-hidden="true" />
                          <span>{n}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="a-softDivider" />
                    <div className="a-miniRow">
                      <div className="a-miniLabel">Outcome</div>
                      <div className="a-miniValue">Operational review without exposing sensitive internals.</div>
                    </div>
                  </Card>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ROUND ENGINE + FAIRNESS */}
          <section className="a-section" id="round-fairness">
            <div className="a-container">
              <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.18 }} variants={stagger}>
                <motion.div variants={fadeUp}>
                  <SectionHeader
                    kicker="Outcomes and artifacts"
                    title="Round Engine + Provably Fair Artifacts."
                    subtitle="The round engine is server-authoritative. Fairness artifacts are produced per round with a commit–reveal posture (conceptually: server commitment, client participation, sequencing), enabling deterministic replay in a sandboxed verifier."
                  />
                </motion.div>

                <motion.div className="a-grid a-grid--diagram" variants={fadeUp}>
                  <Stepper title="Round lifecycle (verification-oriented)" steps={fairnessStepper} />

                  <Card className="a-card--callout" hover={false}>
                    <div className="a-cardTop">
                      <div className="a-cardTitle">What “provably fair” means here</div>
                      <span className="a-chip a-chip--violet">Deterministic</span>
                    </div>
                    <div className="a-body">
                      The platform is designed so that a round produces a set of reproducible artifacts. Verification surfaces can replay a round from committed inputs and compare reproduced results to what was presented—without exposing proprietary game logic or implementation-sensitive details.
                    </div>
                    <div className="a-softDivider" />
                    <div className="a-miniRow">
                      <div className="a-miniLabel">Verification goal</div>
                      <div className="a-miniValue">Prove outcomes via replay, not assertion.</div>
                    </div>
                  </Card>
                </motion.div>

                <motion.div className="a-grid a-grid--diagram" variants={fadeUp}>
                  <Timeline title="Verification UX timeline" items={verificationTimeline} />
                  <DiagramCard
                    title="Artifact chain (conceptual)"
                    lines={[
                      "Tenant → Game → Round → Commitment posture",
                      "Round → Settlement reference → Ledger-linked record",
                      "Round → Verifier metadata → Sandbox replay view",
                      "Round → Export bundle (operator review)",
                    ]}
                    note="This is a conceptual chain for diligence. It is not a schema, does not include formulas, and does not disclose secret handling."
                  />
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* AUDITABILITY */}
          <section className="a-section" id="auditability">
            <div className="a-container">
              <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.18 }} variants={stagger}>
                <motion.div variants={fadeUp}>
                  <SectionHeader
                    kicker="Operational visibility"
                    title="Auditability & Operational Visibility."
                    subtitle="Audit readiness is treated as a platform behavior. Records are structured to support diligence, dispute workflows, and reconciliation without relying on “trust us” explanations."
                  />
                </motion.div>

                <motion.div className="a-grid a-grid--two" variants={stagger}>
                  <motion.div variants={fadeUp}>
                    <Card className="a-card--split" hover={false}>
                      <div className="a-splitHead">
                        <div className="a-cardTitle">What is logged (high-level)</div>
                        <span className="a-chip a-chip--green">Reviewable</span>
                      </div>
                      <Checklist title={null} items={auditLogged} />
                      <div className="a-softDivider" />
                      <div className="a-splitNote">
                        Logging is described at a posture level. This page does not disclose storage schemas or internal representations.
                      </div>
                    </Card>
                  </motion.div>

                  <motion.div variants={fadeUp}>
                    <Card className="a-card--split" hover={false}>
                      <div className="a-splitHead">
                        <div className="a-cardTitle">What can be exported (conceptual)</div>
                        <span className="a-chip a-chip--cyan">Bundles</span>
                      </div>
                      <Checklist title={null} items={auditExported} />
                      <div className="a-softDivider" />
                      <div className="a-splitNote">
                        Exports are structured for operator workflows. Provider internals and secret materials remain out of scope.
                      </div>
                    </Card>
                  </motion.div>
                </motion.div>

                <motion.div className="a-callStrip a-callStrip--wide" variants={fadeUp}>
                  <div className="a-callStripInner">
                    <div className="a-callStripTitle">Tamper-evident posture (conceptual)</div>
                    <div className="a-callStripBody">
                      Records are designed to be reviewable and attributable: round artifacts, settlement references, configuration snapshots, and administrative actions produce a coherent narrative suitable for audits and due diligence review.
                    </div>
                    <a className="a-inlineLink" href="/platform/fairness">
                      Open the Fairness & Transparency Module <span aria-hidden="true">→</span>
                    </a>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* SECURITY POSTURE */}
          <section className="a-section" id="security">
            <div className="a-container">
              <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.18 }} variants={stagger}>
                <motion.div variants={fadeUp}>
                  <SectionHeader
                    kicker="Safe summary"
                    title="Security Boundaries."
                    subtitle="This section summarizes security boundaries at an architectural level. It does not disclose secret handling mechanics, internal routes, or other exploit-enabling details."
                  />
                </motion.div>

                <motion.div className="a-grid a-grid--modules" variants={stagger}>
                  {securityPosture.map((s) => (
                    <motion.div key={s.title} variants={fadeUp}>
                      <Card className="a-card--module">
                        <div className="a-cardTitleRow">
                          <div className="a-cardTitle">{s.title}</div>
                          <span className="a-miniGlow a-miniGlow--red" aria-hidden="true" />
                        </div>
                        <div className="a-body">{s.desc}</div>
                      </Card>
                    </motion.div>
                  ))}
                </motion.div>

                <motion.div className="a-noteBar" variants={fadeUp}>
                  <div className="a-noteBarIcon" aria-hidden="true">ℹ</div>
                  <div className="a-noteBarText">
                    The platform emphasizes server-authoritative outcomes, tenant-scoped boundaries, and traceable artifacts. Security claims here are bounded: no absolutes, no “impossible to hack,” and no disclosure of internal defensive mechanics.
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* DEPLOYMENT */}
          <section className="a-section" id="deployment">
            <div className="a-container">
              <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.18 }} variants={stagger}>
                <motion.div variants={fadeUp}>
                  <SectionHeader
                    kicker="Operations"
                    title="Deployment & Scaling."
                    subtitle="Ethosino is designed for predictable scaling and tenant-aware stability. This section describes deployment posture at a high level without vendor-specific topology."
                  />
                </motion.div>

                <motion.div className="a-grid a-grid--modules" variants={stagger}>
                  {deployment.map((d) => (
                    <motion.div key={d.title} variants={fadeUp}>
                      <Card className="a-card--module">
                        <div className="a-cardTitleRow">
                          <div className="a-cardTitle">{d.title}</div>
                          <span className="a-miniGlow a-miniGlow--violet" aria-hidden="true" />
                        </div>
                        <div className="a-body">{d.desc}</div>
                      </Card>
                    </motion.div>
                  ))}
                </motion.div>

                <motion.div className="a-grid a-grid--diagram" variants={fadeUp}>
                  <Card className="a-card--callout" hover={false}>
                    <div className="a-cardTop">
                      <div className="a-cardTitle">Noisy neighbor posture</div>
                      <span className="a-chip a-chip--cyan">Tenant-aware</span>
                    </div>
                    <div className="a-body">
                      Multi-tenant platforms must preserve stability when one operator scales. Ethosino’s posture emphasizes tenant-scoped controls and observability so operator growth doesn’t become another operator’s outage.
                    </div>
                    <div className="a-softDivider" />
                    <div className="a-miniRow">
                      <div className="a-miniLabel">Goal</div>
                      <div className="a-miniValue">Predictable behavior under load with reviewable operational signals.</div>
                    </div>
                  </Card>

                  <DiagramCard
                    title="Scaling narrative (conceptual)"
                    lines={[
                      "Tenant-scoped controls → stable shared runtime",
                      "Observable flows → reviewable operational posture",
                      "Governed changes → predictable upgrades",
                      "Audit artifacts → diligence-ready platform behavior",
                    ]}
                    note="This describes outcomes and posture, not topology."
                  />
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* SUMMARY + CTA */}
          <section className="a-section a-section--cta" id="summary">
            <div className="a-container">
              <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger}>
                <motion.div variants={fadeUp}>
                  <SectionHeader
                    kicker="Conclusion"
                    title="Built for Diligence."
                    subtitle="Ethosino is designed to produce verifiable outcomes, clean tenant boundaries, ledger consistency, and audit-ready artifacts as default platform behavior."
                  />
                </motion.div>

                <motion.div className="a-ctaStrip" variants={fadeUp}>
                  <div className="a-ctaLeft">
                    <div className="a-ctaTitle">Review the architecture with our team.</div>
                    <div className="a-ctaBody">
                      We’ll walk through tenant boundaries, entitlement enforcement, wallet separation, verifier UX, and export posture—end to end, at a diligence-friendly abstraction level.
                    </div>
                    <div className="a-ctaFine">
                      We support subscription, rev-share, and hybrid structures depending on deployment scope.
                    </div>
                  </div>
                  <div className="a-ctaRight">
                    <Link 
                      to="https://demo.ethosino.com" 
                      className="a-btn a-btn--primary a-btn--lg"
                    >
                      View the LIVE demo <span className="a-arr" aria-hidden="true">→</span>
                    </Link>
                    <a className="a-btn a-btn--ghost a-btn--lg" href="/request-architecture-brief">
                      Request Architecture Brief <span className="a-arr" aria-hidden="true">→</span>
                    </a>
                  </div>
                </motion.div>

                <motion.div className="a-footerMini" variants={fade}>
                  <div className="a-footerMark">ETHOSINO</div>
                  <div className="a-footerLinks">
                    <a className="a-footerLink" href="/platform/fairness">Fairness Module</a>
                    <a className="a-footerLink" href="/architecture">Architecture</a>
                    <a className="a-footerLink" href="/contact">Contact</a>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>
        </div>
      <Footer />
    </Fragment>
  );
}

export default OperatorArchitectureWalkthroughHelper;